import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * One table, used by every list in the console - RX-OPS-08.
 *
 * Every list screen needs the same four things and none of them had any of
 * it: paging, sorting, filtering, and a way to say what "nothing here" means.
 * Built once so a new screen inherits all of it rather than getting none of
 * it, which is how the last six lists shipped.
 *
 * -- Three empty states, not one ------------------------------------------
 * "No results" is not the same as "your filter excluded everything", and
 * neither is the same as "this query failed". Conflating them is the reason
 * somebody stares at a screen convinced the data is gone. Each gets its own
 * message and, where it makes sense, its own way out.
 *
 * -- Sorting is server-side, on indexed columns only ------------------------
 * A column is sortable when the caller says so, and the caller says so only
 * when there is an index behind it. Client-side sorting of a page is worse
 * than none: it reorders the twenty rows you can see and silently implies the
 * other four thousand were considered.
 */

export interface Column<Row> {
  key: string;
  header: string;
  /** How to render the cell. Given the row so it can combine fields. */
  cell: (row: Row) => React.ReactNode;
  /**
   * Sortable only when an index backs it. Sorting an unindexed column is a
   * full table scan wearing a friendly name.
   */
  sortable?: boolean;
  /** Hidden by default; the reader can turn it on. */
  optional?: boolean;
  /** Narrow columns stay narrow. */
  width?: string;
  /** Right-aligned for numbers, so digits line up. */
  numeric?: boolean;
}

export interface SortState {
  key: string;
  direction: 'asc' | 'desc';
}

interface Props<Row> {
  columns: Column<Row>[];
  rows: Row[] | null;
  rowKey: (row: Row) => string;

  /** Null while loading, a message when the query failed. */
  error?: string | null;
  loading?: boolean;

  sort?: SortState | null;
  onSort?: (next: SortState) => void;

  search?: string;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;

  /** Cursor paging. `onNext` is absent when there is no next page. */
  onNext?: (() => void) | null;
  onPrevious?: (() => void) | null;
  /** Said out loud when the server truncated rather than reached the end. */
  truncated?: boolean;

  /** What this list is called, for the empty states. */
  noun?: string;
  /** Shown when there is genuinely nothing, as opposed to nothing matching. */
  emptyAction?: React.ReactNode;

  onRowClick?: (row: Row) => void;
  /** Remembered per screen, so a reader's column choice survives a reload. */
  storageKey?: string;
}

export function DataTable<Row>({
  columns, rows, rowKey, error = null, loading = false,
  sort = null, onSort, search, onSearch, searchPlaceholder = 'Search…',
  onNext = null, onPrevious = null, truncated = false,
  noun = 'rows', emptyAction, onRowClick, storageKey,
}: Props<Row>) {
  const [visible, setVisible] = useState<Set<string>>(() => defaultVisible(columns, storageKey));
  const [pickerOpen, setPickerOpen] = useState(false);
  const picker = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!storageKey) return;
    try {
      /*
       * Store what is HIDDEN, not what is shown.
       *
       * A snapshot of the visible set freezes the table as it was the day
       * somebody last touched the Columns menu: a column added in a later
       * release is not in that list, so it never appears — for that reader,
       * permanently, with nothing to indicate a column exists at all. Storing
       * the deviation from the default means a new column is visible by
       * default, which is what "default" has to mean.
       */
      const hidden = columns.filter((c) => !visible.has(c.key)).map((c) => c.key);
      localStorage.setItem(
        `drbell.cols.${storageKey}`,
        JSON.stringify({ hidden }),
      );
    } catch {
      // A remembered column choice is a convenience. Losing it must not
      // break the table.
    }
  }, [visible, storageKey, columns]);

  useEffect(() => {
    if (!pickerOpen) return;
    const onAway = (e: MouseEvent) => {
      if (picker.current && !picker.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onAway);
    return () => document.removeEventListener('mousedown', onAway);
  }, [pickerOpen]);

  const shown = useMemo(() => columns.filter((c) => visible.has(c.key)), [columns, visible]);
  const searching = Boolean(search && search.trim());

  return (
    <div className="space-y-3">
      {(onSearch || columns.some((c) => c.optional)) && (
        <div className="flex flex-wrap items-center gap-2">
          {onSearch && (
            <input
              value={search ?? ''}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="min-h-11 flex-1 rounded-lg border border-(--line) bg-(--bg) px-3 text-sm outline-none focus:border-(--accent)"
            />
          )}
          {columns.some((c) => c.optional) && (
            <div className="relative" ref={picker}>
              <button
                onClick={() => setPickerOpen((v) => !v)}
                aria-expanded={pickerOpen}
                className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
              >
                Columns
              </button>
              {pickerOpen && (
                <div className="absolute right-0 z-10 mt-1 w-56 rounded-lg border border-(--line) bg-(--panel) p-2 shadow-lg">
                  {columns.map((c) => (
                    <label key={c.key} className="flex min-h-9 cursor-pointer items-center gap-2 px-1 text-sm">
                      <input
                        type="checkbox"
                        checked={visible.has(c.key)}
                        onChange={(e) => {
                          const next = new Set(visible);
                          if (e.target.checked) next.add(c.key);
                          else next.delete(c.key);
                          setVisible(next);
                        }}
                      />
                      {c.header}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-(--line)">
        <table className="w-full text-sm">
          <thead className="bg-(--panel) text-left text-xs text-(--muted)">
            <tr>
              {shown.map((c) => (
                <th
                  key={c.key}
                  style={c.width ? { width: c.width } : undefined}
                  className={`p-3 font-medium ${c.numeric ? 'text-right' : ''}`}
                >
                  {c.sortable && onSort ? (
                    <button
                      onClick={() =>
                        onSort({
                          key: c.key,
                          direction: sort?.key === c.key && sort.direction === 'asc' ? 'desc' : 'asc',
                        })
                      }
                      className="inline-flex items-center gap-1 hover:text-(--fg)"
                      aria-sort={
                        sort?.key === c.key
                          ? sort.direction === 'asc' ? 'ascending' : 'descending'
                          : 'none'
                      }
                    >
                      {c.header}
                      <span aria-hidden="true" className={sort?.key === c.key ? 'text-(--accent)' : 'opacity-30'}>
                        {sort?.key === c.key && sort.direction === 'desc' ? '↓' : '↑'}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {/*
              Three distinct states. Which one a reader is looking at changes
              what they should do next, and a single "no data" message tells
              them none of it.
            */}
            {error && (
              <tr>
                <td colSpan={shown.length} className="p-8 text-center">
                  <p className="text-sm text-red-500">{error}</p>
                  <p className="mt-1 text-xs text-(--muted)">
                    This is a failure to load, not an empty list. Nothing has been deleted.
                  </p>
                </td>
              </tr>
            )}

            {!error && (loading || rows === null) && (
              <tr>
                <td colSpan={shown.length} className="p-8 text-center text-sm text-(--muted)">
                  Loading…
                </td>
              </tr>
            )}

            {!error && !loading && rows?.length === 0 && (
              <tr>
                <td colSpan={shown.length} className="p-8 text-center">
                  {searching ? (
                    <>
                      <p className="text-sm">Nothing matches that search.</p>
                      <button
                        onClick={() => onSearch?.('')}
                        className="mt-2 min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
                      >
                        Clear the search
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-(--muted)">No {noun} yet.</p>
                      {emptyAction && <div className="mt-3">{emptyAction}</div>}
                    </>
                  )}
                </td>
              </tr>
            )}

            {!error && !loading && rows?.map((row) => (
              /*
               * A clickable row has to be reachable without a mouse.
               *
               * onClick on a <tr> alone is invisible to the keyboard and to a
               * screen reader: there is nothing to tab to and nothing that
               * announces itself as pressable, so the only way to approve an
               * organisation was to point at it. tabIndex + role + Enter/Space
               * is the standard remedy (WCAG 2.1.1), and the focus ring makes
               * the current row visible while tabbing.
               */
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        // A click on a button inside the row must not also
                        // fire the row, and Space must not scroll the page.
                        if (e.target !== e.currentTarget) return;
                        e.preventDefault();
                        onRowClick(row);
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                role={onRowClick ? 'button' : undefined}
                className={`border-t border-(--line) ${
                  onRowClick
                    ? 'cursor-pointer hover:bg-(--panel) focus:bg-(--panel) focus:outline-2 focus:-outline-offset-2 focus:outline-(--accent)'
                    : ''
                }`}
              >
                {shown.map((c) => (
                  <td key={c.key} className={`p-3 align-top ${c.numeric ? 'text-right tabular-nums' : ''}`}>
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(onPrevious || onNext || truncated) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {truncated && (
            /* Said rather than implied. A list that silently stops at a cap
               is defect 18, which this repo has now hit four times. */
            <span className="text-amber-600">
              Showing the first page only — narrow the search to see the rest.
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => onPrevious?.()}
              disabled={!onPrevious}
              className="min-h-11 rounded-lg border border-(--line) px-3 hover:border-(--accent) disabled:opacity-40"
            >
              ← Previous
            </button>
            <button
              onClick={() => onNext?.()}
              disabled={!onNext}
              className="min-h-11 rounded-lg border border-(--line) px-3 hover:border-(--accent) disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Which columns to show, given the defaults and what this reader hid.
 *
 * Exported and pure so the migration below can be tested. It was not, and
 * that is how a stored preference silently deleted a new column from a table
 * for every existing reader — defect 92.
 */
export function resolveVisibleColumns<Row>(
  columns: Column<Row>[],
  saved: string | null,
): Set<string> {
  const byDefault = () => new Set(columns.filter((c) => !c.optional).map((c) => c.key));
  if (!saved) return byDefault();

  try {
    const parsed = JSON.parse(saved) as unknown;

    if (Array.isArray(parsed)) {
      /*
       * The old format: a list of visible keys.
       *
       * It cannot say which columns existed when it was written, so a key
       * missing from it is either "hidden on purpose" or "did not exist yet"
       * and there is no way to tell. Read it as the former only for OPTIONAL
       * columns — the ones a reader plausibly turned off — and let anything
       * else default to visible. One reader losing one remembered toggle is
       * nothing; a column nobody can see is the bug.
       */
      const legacy = new Set(parsed as string[]);
      return new Set(
        columns.filter((c) => (c.optional ? legacy.has(c.key) : true)).map((c) => c.key),
      );
    }

    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { hidden?: unknown }).hidden)) {
      const hidden = new Set((parsed as { hidden: string[] }).hidden);
      const visible = columns.filter((c) => !hidden.has(c.key)).map((c) => c.key);
      // Never leave a table with no columns at all, whatever is stored.
      return visible.length > 0 ? new Set(visible) : byDefault();
    }
  } catch {
    // Fall through to the defaults.
  }
  return byDefault();
}

function defaultVisible<Row>(columns: Column<Row>[], storageKey?: string): Set<string> {
  if (!storageKey) return resolveVisibleColumns(columns, null);
  try {
    return resolveVisibleColumns(columns, localStorage.getItem(`drbell.cols.${storageKey}`));
  } catch {
    // Private browsing, or storage disabled.
    return resolveVisibleColumns(columns, null);
  }
}
